
import Vue from 'vue'
import gsap from 'gsap'
import { mapper } from '@/stores/store'
import { API_STATUS_PENDING, API_STATUS_SUCCESS, API_STATUS_FAILURE } from '@/constants'
import CrossButton from '@/components/IconButtons/CrossButton.vue'
import ScidButton from '@/components/Buttons/ScidButton.vue'
import ScidUserBar from '@/components/ScidUserBar.vue'
import RectangleButton from '@/components/Buttons/RectangleButton.vue'
import Loader from '@/components/Loader.vue'

export default Vue.extend({
  name: 'UploadModal',
  components: {
    CrossButton,
    ScidButton,
    ScidUserBar,
    RectangleButton,
    Loader,
  },
  props: {
    opened: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      API_STATUS_PENDING,
      transitionOut: false,
      loaded: false,
      saveCompleted: false,
      pinBlob: '',
    }
  },
  computed: {
    ...mapper('user', ['isLoggedIn', 'userId', 'isUploadCooldown', 'profileImageUploadCooldown']),
    ...mapper('pin', ['saveStatus']),
    ...mapper('auth', ['authenticateStatus', 'logoutStatus', 'ssoInited']),
    isSaving(): boolean {
      return this.saveStatus === API_STATUS_PENDING
    },
    saveButtonLabel(): string {
      return !this.isSaving ? 'save_pin_cta' : 'save_pin_progress'
    },
    saveSuccessfull(): boolean {
      return this.saveStatus === API_STATUS_SUCCESS
    },
    saveFailure(): boolean {
      return this.saveStatus === API_STATUS_FAILURE
    },
    cooldownMessage(): string {
      if (this.isUploadCooldown) {
        let hours = Math.max(Math.ceil(this.profileImageUploadCooldown / 60 / 60), 1)
        let hoursPluralization = hours === 1 ? this.$t('save_error_hour') : this.$t('save_error_hours')
        return `${this.$t('save_error_ddos_hours')} ${hours} ${hoursPluralization}.`
      } else {
        return ''
      }
    },
    isVisible(): boolean {
      // Opening - wait for pin to finish generating
      // Closing - wait for out transition
      return (this.$props.opened && this.pinBlob !== '') || this.transitionOut
    },
    isLoggingIn(): boolean {
      return this.authenticateStatus === API_STATUS_PENDING
    },
    isLoggingOut(): boolean {
      return this.logoutStatus === API_STATUS_PENDING
    },
    isLoading(): boolean {
      return !this.loaded || !this.ssoInited || this.isLoggingIn || this.isLoggingOut
    }
  },
  methods: {
    ...mapper('pin', ['getUploadPinBlob', 'savePin']),
    ...mapper('auth', ['ssoLogin']),
    ...mapper('user', ['getUser']),
    onEnter(el: any, done: any) {
      gsap.fromTo(
        this.$refs.pinImage as any,
        { opacity: 0, scale: 0.75 },
        { duration: 0.25, opacity: 1, scale: 1, ease: 'power2.inOut' }
      )
    },
    onLeave(el: any, done: any) {
      gsap.to(this.$refs.pinImage as any, {
        duration: 0.2,
        opacity: 0,
        scale: 0.75,
        ease: 'power2.in',
      })
      gsap.to(this.$refs.pinImage as any, { delay: 0.2, visibility: 'hidden' })
    },
    getUserData() {
      this.loaded = false

      // Check users logged in status / get user data
      this.getUser()
        .then(() => {
          this.loaded = true
        })
        .catch(() => {
          // Continue as logged out
          this.loaded = true
        })
    },
    async updatePinBlob() {
      this.getUploadPinBlob()
        .then(response => {
          this.pinBlob = response
        })
        .catch(() => {})
    },
    onClickLogin() {
      this.ssoLogin()
    },
    onClickSave() {
      this.savePin()
        .then(() => {
          this.saveCompleted = true
        })
        .catch(() => {
          this.saveCompleted = true
        })
    },
    onClickRestart() {
      this.saveCompleted = false
    },
    onClickClose() {
      this.$emit('close')
    },
    clear() {
      this.loaded = false
      this.saveCompleted = false
      this.pinBlob = ''
    },
  },
  watch: {
    opened: function (newValue, oldValue) {
      if (newValue) {
        // Update user data when modal is opened (if logged in and not loading)
        if (this.isLoggedIn && this.ssoInited && !this.isLoggingIn && !this.isLoggingOut) {
          this.getUserData()
        } else {
          this.loaded = true
        }

        this.updatePinBlob()
      } else {
        this.transitionOut = true

        // Wait until out transition is completed before unmount
        setTimeout(() => {
          this.clear()
          this.transitionOut = false
        }, 300)
      }
    }
  },
})
